/**
 * Service Name: API Service
 * Module: frontend/src/services
 *
 * Description:
 * The API Service is the core HTTP client utility for the ConnectPath frontend application,
 * providing a centralized, pre-configured axios instance for all API communications with the
 * backend services. It implements sophisticated request/response interceptors for authentication,
 * automatic token refresh, and comprehensive error handling. This service ensures secure,
 * reliable, and consistent communication between the frontend and backend systems while
 * adhering to the ConnectPath Modular Methodology's integration standards.
 *
 * Responsibilities:
 * - Provide a centralized axios instance for all API requests
 * - Automatically inject authentication tokens into request headers
 * - Handle token expiration and automatic refresh workflows
 * - Implement retry logic for failed authentication attempts
 * - Standardize error handling across the application
 * - Support CORS with proper credential handling
 * - Configure base URLs and default headers
 * - Provide utility functions for error message extraction
 *
 * Dependencies:
 * - axios: HTTP client library for making requests
 * - TokenManager: Service for JWT token management
 * - Environment variables: For API URL configuration
 *
 * @example
 * ```typescript
 * import { api, handleApiError } from './services/api';
 * 
 * // Making a GET request
 * try {
 *   const { data } = await api.get('/churches/123');
 *   console.log('Church data:', data);
 * } catch (error) {
 *   const errorMessage = handleApiError(error);
 *   console.error('Failed to fetch church:', errorMessage);
 * }
 * 
 * // Making a POST request with data
 * try {
 *   const { data } = await api.post('/assessments', {
 *     title: 'New Assessment',
 *     questions: [...]
 *   });
 *   console.log('Created assessment:', data);
 * } catch (error) {
 *   const errorMessage = handleApiError(error);
 *   console.error('Failed to create assessment:', errorMessage);
 * }
 * ```
 *
 * Notes:
 * - All requests automatically include authentication tokens if available
 * - Failed requests due to 401 errors trigger automatic token refresh
 * - Only one token refresh attempt is made per request to prevent loops
 * - Failed token refresh redirects user to login page
 * - CORS credentials are included for cross-origin requests
 *
 * @module
 */

import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import { enhancedTokenManager } from './EnhancedTokenManager';
import { config } from '../config/environment';
import { unifiedApi } from './UnifiedApiService';

// Extend AxiosRequestConfig to include _retry flag
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

// Helper function to get CSRF token from cookies
function getCsrfToken(): string | null {
  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${config.csrfCookieName}=`));

  return cookie ? cookie.split('=')[1] : null;
}

// Use the unified API instance instead of creating a separate one
// This ensures all API calls use the same interceptors and configuration
// UnifiedApiService already has all the necessary interceptors configured:
// - Authentication token injection
// - X-Selected-Church-ID header for church context
// - CSRF token handling
// - 401 error handling with token refresh
export const api: AxiosInstance = unifiedApi.getAxiosInstance();

/**
 * Helper function for extracting user-friendly error messages from API errors.
 * Handles various error scenarios including server errors, network errors, and client errors.
 * 
 * @param error - The error object from a failed API request
 * @returns A user-friendly error message string
 * 
 * @example
 * ```typescript
 * try {
 *   await api.get('/some-endpoint');
 * } catch (error) {
 *   const message = handleApiError(error);
 *   alert(message); // Shows user-friendly error message
 * }
 * ```
 */
export const handleApiError = (error: any): string => {
  if (error.response) {
    // The request was made and the server responded with an error status
    if (error.response.data && error.response.data.message) {
      return error.response.data.message;
    }
    return `Error: ${error.response.status} ${error.response.statusText}`;
  } else if (error.request) {
    // The request was made but no response was received
    return 'No response received from server. Please check your connection.';
  } else {
    // Something happened in setting up the request
    return error.message || 'An unknown error occurred';
  }
};